/****************
 * THE ASSET PAGE'S ORDER DOTS, ONE RULE FOR BOTH APPS. 2026-10-09.
 *
 * The server groups the listed traders' orders by candle and price band
 * (GET /api/v1/assets/{slug}/orders, backend rest/asset_orders.go); this file
 * says how a group is drawn, so the web and the phone draw the same dot.
 *
 * Size: one order is a small dot; a group grows with the square root of its
 * count (area, not radius, carries the count), capped so a crowd stays a dot.
 * Colour: long or short, whichever the group holds more of; blue and amber,
 * because green and red mean profit and loss everywhere else in the app.
 * Fill: opens (and increases) filled, closes (and decreases) hollow, by majority.
 * The count is printed on a dot big enough to hold it.
 *
 * Byte-identical in frontend/src/lib and mobile-app/src/lib
 * (scripts/sync-mobile-locales.mjs SHARED). Imports nothing.
 ****************/

export interface AssetOrderGroup {
  /** Candle start, ms. */
  t: number;
  count: number;
  long: number;
  short: number;
  open: number;
  close: number;
  /** Buys (long opened/added, short closed/reduced) and sells (the reverse). */
  buy?: number;
  sell?: number;
  price_lo: number;
  price_hi: number;
  price_avg: number;
}

export interface AssetOrderGroups {
  interval: number;
  from: number;
  to: number;
  total: number;
  groups: AssetOrderGroup[];
}

export interface AssetOrder {
  trader_uid: string;
  trader_name: string;
  side: string;
  action: string;
  price: number;
  at: string;
  trade_id?: string;
}

export const ORDER_DOT_LONG = '#3B82F6';
export const ORDER_DOT_SHORT = '#F59E0B';

export function orderDotRadius(count: number): number {
  if (count <= 1) return 3;
  return Math.min(14, 3 + 2.2 * Math.sqrt(count));
}

export function orderDotStyle(g: AssetOrderGroup): { r: number; color: string; filled: boolean; label: string | null } {
  const r = orderDotRadius(g.count);
  return {
    r,
    /* Colour = the pressure on the price (Guillaume, 2026-10-15): closing a
       long is a sell, closing a short a buy. Fill = open vs close. A server
       without buy/sell falls back to the position's side. */
    color: (g.buy != null && g.sell != null ? g.buy >= g.sell : g.long >= g.short) ? ORDER_DOT_LONG : ORDER_DOT_SHORT,
    filled: g.open >= g.close,
    label: r >= 9 ? (g.count > 999 ? '999+' : String(g.count)) : null,
  };
}

/** The tapped group's own orders: its candle, its price range. */
export function orderGroupQuery(g: AssetOrderGroup, intervalSec: number): string {
  return `from=${g.t}&to=${g.t + intervalSec * 1000}&lo=${g.price_lo}&hi=${g.price_hi}`;
}
